// @ts-nocheck
import {
  CFG,
  spellSfx,
} from "../../data";
import {
  Sound,
} from "../../audio";
import {
  TAU,
  clamp,
  rand,
} from "../../util";
import { compact } from "../../entities";
import { Game } from "../game-base";

Game.HR_DRAW_DUR = 0.42;
Game.HR_CLOSE_DUR = 0.42; // flames + outer swirls shrink shut; void stays full size and fades
Game.HR_THICK = 22;

Game.prototype.pentagramVerts = function (hr) {
  const verts = [];
  for (let i = 0; i < 5; i++) {
    const a = hr.rot - Math.PI / 2 + i * TAU / 5;
    verts.push({ x: hr.x + hr.r * Math.cos(a), y: hr.y + hr.r * Math.sin(a) });
  }
  return verts;
};

Game.prototype.pentagramEdges = function (hr) {
  const verts = this.pentagramVerts(hr);
  const edges = [];
  for (let i = 0; i < 5; i++) {
    const a = verts[i];
    const b = verts[(i + 2) % 5];
    const mx = (a.x + b.x) * 0.5;
    const my = (a.y + b.y) * 0.5;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const edgeLen = Math.hypot(dx, dy) || 1;
    edges.push({
      x: mx, y: my,
      dx: dx / edgeLen, dy: dy / edgeLen,
      hx: edgeLen * 0.5,
      hy: Game.HR_THICK,
    });
  }
  return edges;
};

Game.prototype.hellRipEdgePoint = function (edge, lx) {
  return { x: edge.x + lx * edge.dx, y: edge.y + lx * edge.dy };
};

Game.prototype.hellRipDrawFrac = function (hr) {
  if (hr.arm > 0) return clamp(1 - hr.arm / Game.HR_DRAW_DUR, 0, 1);
  if (hr.life != null && hr.life < Game.HR_CLOSE_DUR) {
    return clamp(hr.life / Game.HR_CLOSE_DUR, 0, 1);
  }
  return 1;
};

Game.prototype.hellRipFade = function (hr) {
  const df = this.hellRipDrawFrac(hr);
  if (hr.arm > 0) return 0.55 + 0.45 * df;
  if (hr.life != null && hr.life < Game.HR_CLOSE_DUR) return df;
  return 1;
};

Game.prototype.ensurePlatformTex = function () {
  if (!this._platformTex || this._platformTexR !== this.maxRadius) this.bakePlatformTex();
};

Game.prototype.activeTwistFieldCount = function () {
  let n = (this.hellrips && this.hellrips.length) || 0;
  for (const sg of this.sigils || []) {
    if (sg.spellId === "voidrip") n++;
  }
  return n;
};

/** Coarser twist mesh when many void/hell rips are on screen (each field is rings×segs drawImage calls). */
Game.prototype.twistFieldMesh = function () {
  const n = this.activeTwistFieldCount();
  if (n >= 12) return { rings: 3, segs: 8, glowRings: 2, glowBands: 2 };
  if (n >= 6) return { rings: 4, segs: 10, glowRings: 3, glowBands: 2 };
  if (n >= 3) return { rings: 4, segs: 12, glowRings: 3, glowBands: 3 };
  return { rings: 5, segs: 16, glowRings: 4, glowBands: 3 };
};

Game.prototype.hellRipFlameStep = function () {
  const n = (this.hellrips && this.hellrips.length) || 0;
  if (n > 10) return Game.FW_FLAME_STEP * 2;
  if (n > 1) return Game.FW_FLAME_STEP * 1.5;
  return Game.FW_FLAME_STEP;
};

// Warp arena stone into a spiralling twist (void rip + hell rip pull band).
Game.prototype.drawArenaTwistField = function (c, opts) {
  this.ensurePlatformTex();
  const tex = this._platformTex;
  const texR = this._platformTexR || this.maxRadius;
  const ax = this.ax || 0;
  const ay = this.ay || 0;
  const {
    cx, cy, coreR, outerR, open, fade, t,
    pulse = 0, armed = true,
  } = opts;

  if (outerR - coreR < 6) return;

  const spin = t * (armed ? 2.2 : 0.75) + pulse * 0.2;
  const wobble = 0.08 * Math.sin(t * 3.1 + pulse);
  const maxTwist = (1.35 + wobble) * open * fade;

  const mesh = this.twistFieldMesh();
  const rings = mesh.rings;
  const segs = mesh.segs;

  c.save();
  c.globalCompositeOperation = "source-over";

  for (let ri = 0; ri < rings; ri++) {
    const u0 = ri / rings;
    const u1 = (ri + 1) / rings;
    const r0 = coreR + (outerR - coreR) * u0;
    const r1 = coreR + (outerR - coreR) * u1;
    const t0 = maxTwist * (1 - u0) * (1 - u0);
    const t1 = maxTwist * (1 - u1) * (1 - u1);
    const ringAlpha = fade * open * (0.42 + 0.58 * u1);

    for (let si = 0; si < segs; si++) {
      const segA = TAU / segs;
      const a0 = si * segA + spin;
      const a1 = a0 + segA;

      const x00 = cx + Math.cos(a0 + t0) * r0;
      const y00 = cy + Math.sin(a0 + t0) * r0;
      const x01 = cx + Math.cos(a1 + t0) * r0;
      const y01 = cy + Math.sin(a1 + t0) * r0;
      const x11 = cx + Math.cos(a1 + t1) * r1;
      const y11 = cy + Math.sin(a1 + t1) * r1;
      const x10 = cx + Math.cos(a0 + t1) * r1;
      const y10 = cy + Math.sin(a0 + t1) * r1;

      c.save();
      c.beginPath();
      c.moveTo(x00, y00);
      c.lineTo(x01, y01);
      c.lineTo(x11, y11);
      c.lineTo(x10, y10);
      c.closePath();
      c.clip();
      c.globalAlpha = ringAlpha;
      c.drawImage(tex, ax - texR, ay - texR, texR * 2, texR * 2);
      c.restore();
    }
  }

  c.save();
  c.translate(cx, cy);
  const voidTint = c.createRadialGradient(0, 0, coreR, 0, 0, outerR);
  voidTint.addColorStop(0, `rgba(5,4,12,${0.62 * fade * open})`);
  voidTint.addColorStop(0.35, `rgba(36,18,52,${0.34 * fade * open})`);
  voidTint.addColorStop(0.72, `rgba(18,12,28,${0.14 * fade * open})`);
  voidTint.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = voidTint;
  c.beginPath();
  c.arc(0, 0, outerR, 0, TAU);
  c.arc(0, 0, coreR, 0, TAU, true);
  c.fill("evenodd");

  c.globalCompositeOperation = "lighter";
  for (let band = 0; band < mesh.glowBands; band++) {
    const phase = spin * 1.15 + band * (TAU / mesh.glowBands);
    for (let ri = 0; ri < mesh.glowRings; ri++) {
      const rf = (ri + 0.55) / mesh.glowRings;
      const rr = coreR + (outerR - coreR) * rf;
      const twist = maxTwist * (1 - rf) * (1 - rf);
      const span = 0.28 + 0.1 * Math.sin(t * 2.4 + band + ri);
      const mid = phase + twist;
      c.globalAlpha = fade * open * 0.055 * (0.45 + rf);
      c.strokeStyle = "rgba(190,170,255,0.85)";
      c.lineWidth = 1.2 + rf * 1.4;
      c.lineCap = "round";
      c.beginPath();
      c.arc(0, 0, rr, mid - span, mid + span);
      c.stroke();
    }
  }
  c.restore();

  c.globalAlpha = 1;
  c.globalCompositeOperation = "source-over";
  c.restore();
};

Game.prototype.hellRipStarShimmer = function (s, t) {
  const tw = s.tw || 1;
  const p = s.p || 0;
  const slow = 0.5 + 0.5 * Math.sin(t * tw + p);
  const fast = 0.5 + 0.5 * Math.sin(t * tw * 2.6 + p * 1.4);
  return {
    a: 0.62 + 0.38 * slow * fast,
    sz: 0.88 + 0.12 * Math.sin(t * tw * 1.8 + p * 0.9),
  };
};

Game.prototype.hellRipRingPoint = function (hr, ang) {
  const a = hr.rot - Math.PI / 2 + ang;
  return { x: hr.x + hr.r * Math.cos(a), y: hr.y + hr.r * Math.sin(a) };
};

Game.prototype.voidFieldFillStars = function (obj, fieldR) {
  if (obj.fillStars) return obj.fillStars;
  const stars = [];
  const n = Math.max(24, Math.floor((fieldR * fieldR) / 360));
  let seed = Math.abs(Math.sin(obj.x * 12.9898 + obj.y * 78.233) * 43758.5453) % 1;
  for (let i = 0; i < n; i++) {
    seed = (seed * 16807 + 0.123) % 1;
    const ang = seed * TAU;
    seed = (seed * 16807 + 0.456) % 1;
    const dist = Math.sqrt(seed) * 0.94;
    seed = (seed * 16807 + 0.789) % 1;
    stars.push({
      nx: Math.cos(ang) * dist,
      ny: Math.sin(ang) * dist,
      r: 0.55 + seed * 1.6,
      a: 0.55 + seed * 0.45,
      tw: 0.45 + seed * 2.1,
      p: seed * TAU,
    });
  }
  obj.fillStars = stars;
  return stars;
};

Game.prototype.hellRipFillStars = function (hr) {
  return this.voidFieldFillStars(hr, hr.r);
};

Game.prototype.drawVoidField = function (c, obj, fade, t, r) {
  const W = this.W;
  const H = this.H;
  const scale = this.scale || 1;
  const scaleY = this.scaleY || scale;
  const px = this.camX * CFG.starParallax;
  const py = this.camY * CFG.starParallax;
  const r2 = r * r;

  c.fillStyle = "#05060d";
  c.fillRect(-r, -r, r * 2, r * 2);

  c.globalCompositeOperation = "source-over";

  for (const s of this.stars || []) {
    let sx = ((s.x - px) % W + W) % W;
    let sy = ((s.y - py) % H + H) % H;
    const wx = this.camX + (sx - this.cx) / scale;
    const wy = this.camY + (sy - this.cy) / scaleY;
    const lx = wx - obj.x;
    const ly = wy - obj.y;
    if (lx * lx + ly * ly > r2) continue;
    if (Math.abs(Math.sin(lx * 12.9898 + ly * 78.233)) < 0.5) continue;
    const sh = this.hellRipStarShimmer(s, t);
    const a = Math.min(1, s.a * 1.25 * fade * sh.a);
    c.globalAlpha = a;
    c.fillStyle = "#cdd6ff";
    const sr = Math.max(0.5, (s.r * 1.1 * sh.sz) / scale);
    c.beginPath(); c.arc(lx, ly, sr, 0, TAU); c.fill();
  }

  c.globalCompositeOperation = "lighter";
  for (const s of this.voidFieldFillStars(obj, r)) {
    const lx = s.nx * r;
    const ly = s.ny * r;
    const sh = this.hellRipStarShimmer(s, t);
    const a = Math.min(1, s.a * fade * sh.a);
    c.globalAlpha = a;
    c.fillStyle = "#cdd6ff";
    c.beginPath(); c.arc(lx, ly, s.r * sh.sz, 0, TAU); c.fill();
    c.globalAlpha = a * 0.5;
    c.beginPath(); c.arc(lx, ly, s.r * 0.42 * sh.sz, 0, TAU); c.fill();
  }

  c.globalAlpha = 1;
  c.globalCompositeOperation = "source-over";

  const edge = c.createRadialGradient(0, 0, r * 0.72, 0, 0, r);
  edge.addColorStop(0, "rgba(0,0,0,0)");
  edge.addColorStop(1, `rgba(0,0,0,${0.35 * fade})`);
  c.fillStyle = edge;
  c.beginPath(); c.arc(0, 0, r, 0, TAU); c.fill();
};

Game.prototype.drawHellRipVoidField = function (c, hr, fade, t, r) {
  return this.drawVoidField(c, hr, fade, t, r);
};

Game.prototype.drawVoidFieldCore = function (c, obj, fade, t, drawFrac, coreFrac) {
  const r = obj.r * drawFrac * coreFrac;
  if (r < 6) return;
  c.save();
  c.translate(obj.x, obj.y);
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.clip();
  this.drawVoidField(c, obj, fade, t, r);
  c.restore();
};

Game.prototype.drawHellRipVoidCore = function (c, hr, fade, t) {
  const r = hr.r;
  if (r < 6) return;
  c.save();
  c.translate(hr.x, hr.y);
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.clip();
  this.drawVoidField(c, hr, fade, t, r);
  c.restore();
};

// Same silhouette as firewall flames, with gentler sway/flicker for hell rip.
Game.prototype.drawHellRipFlame = function (c, hy, fade, pulse, t, seed) {
  const flick = 0.5 + 0.5 * Math.sin(t * 8 + seed);
  const sway = Math.sin(t * 3 + seed * 0.7) * 1.1;
  const h = hy * (2.05 + 0.32 * flick) * fade * (0.88 + 0.1 * Math.sin(seed * 0.19));
  const w = 4.5 + flick * 2;

  c.save();
  c.translate(sway, hy * 0.06);
  c.globalCompositeOperation = "lighter";

  const body = c.createLinearGradient(0, 0, 0, -h);
  body.addColorStop(0, `rgba(220,55,10,${fade * (0.48 + 0.1 * pulse)})`);
  body.addColorStop(0.42, `rgba(255,120,30,${fade * (0.4 + 0.08 * pulse)})`);
  body.addColorStop(0.74, `rgba(255,200,100,${fade * (0.26 + 0.07 * pulse)})`);
  body.addColorStop(1, "rgba(255,240,200,0)");
  c.fillStyle = body;
  c.beginPath();
  c.moveTo(-w, 0);
  c.quadraticCurveTo(-w * 0.32, -h * 0.6, 0, -h);
  c.quadraticCurveTo(w * 0.32, -h * 0.6, w, 0);
  c.closePath();
  c.fill();

  c.globalAlpha = fade * (0.5 + 0.18 * pulse);
  c.fillStyle = "rgba(255,230,160,0.72)";
  c.beginPath();
  c.moveTo(-w * 0.26, 0);
  c.quadraticCurveTo(0, -h * 0.56, w * 0.26, 0);
  c.closePath();
  c.fill();

  c.restore();
};

Game.prototype.spawnHellRipEmber = function (hr, x, y, hy) {
  if (!hr.emberParts) hr.emberParts = [];
  hr.emberParts.push({
    x, y,
    rise: rand(0, hy * 0.45),
    drift: rand(-6, 6),
    riseV: rand(70, 130),
    life: rand(0.28, 0.55), maxLife: 0.55,
    size: rand(2.5, 5),
    col: Math.random() < 0.55 ? "#ffd479" : "#ff7a2f",
    smoke: Math.random() < 0.22,
  });
};

Game.prototype.updateHellRipEmbers = function (dt) {
  const active = this.state === "playing" || this.state === "countdown";
  const cap = Game.FW_EMBER_MAX;
  const hy = Game.HR_THICK;
  for (const hr of this.hellrips || []) {
    if (!hr.emberParts) hr.emberParts = [];
    const drawFrac = this.hellRipDrawFrac(hr);
    const fade = this.hellRipFade(hr);
    if (!active || fade <= 0 || drawFrac < 0.12) continue;

    const rate = Game.FW_EMBER_RATE * fade * clamp(drawFrac * 2.2, 0.25, 1);
    hr._emberAcc = (hr._emberAcc || 0) + dt * rate * 0.55;
    while (hr._emberAcc >= 1 && hr.emberParts.length < cap) {
      hr._emberAcc -= 1;
      const edges = this.pentagramEdges(hr);
      const edge = edges[Math.floor(Math.random() * edges.length)];
      const visHx = edge.hx * drawFrac;
      const lx = rand(-visHx * 0.88, visHx * 0.88);
      if (this.fireWallEdgeFade(lx, visHx) < 0.08) continue;
      const pt = this.hellRipEdgePoint(edge, lx);
      this.spawnHellRipEmber(hr, pt.x, pt.y, hy);
    }

    if (hr.arm <= 0 && drawFrac >= 0.85) {
      hr._ringEmberAcc = (hr._ringEmberAcc || 0) + dt * rate * 0.4;
      while (hr._ringEmberAcc >= 1 && hr.emberParts.length < cap) {
        hr._ringEmberAcc -= 1;
        const pt = this.hellRipRingPoint(hr, rand(0, TAU));
        this.spawnHellRipEmber(hr, pt.x, pt.y, hy);
      }
    }

    compact(hr.emberParts, p => {
      p.life -= dt;
      p.x += p.drift * dt;
      p.rise += p.riseV * dt;
      return p.life > 0 && p.rise < hy * 6;
    });
  }
};

Game.prototype.drawHellRipEmber = function (c, p, fade) {
  const a = clamp(p.life / (p.maxLife || 1), 0, 1) * fade;
  if (a < 0.04) return;
  const born = Math.sin(clamp(1 - p.life / (p.maxLife || 1), 0, 1) * Math.PI);
  c.save();
  c.translate(p.x, p.y - p.rise);
  if (p.smoke) {
    c.globalAlpha = a * 0.35 * born;
    c.fillStyle = "#2a2018";
    const r = p.size * (1 + (1 - a) * 1.2);
    c.beginPath(); c.arc(0, 0, r, 0, TAU); c.fill();
  } else {
    c.globalCompositeOperation = "lighter";
    c.globalAlpha = a * 0.5;
    c.fillStyle = p.col;
    c.beginPath(); c.arc(0, 0, p.size * 1.35, 0, TAU); c.fill();
    c.globalAlpha = a * 0.85;
    c.beginPath(); c.arc(0, 0, p.size * 0.65, 0, TAU); c.fill();
  }
  c.restore();
};

Game.prototype.drawHellRipEdgeFlames = function (c, edge, visHx, fade, pulse, t, hy, flameFade, seedOff) {
  const step = this.hellRipFlameStep();
  for (let lx = -visHx + step * 0.5; lx <= visHx; lx += step) {
    const edgeAmt = this.fireWallEdgeFade(lx, visHx);
    if (edgeAmt < 0.05) continue;
    const jitter = Math.sin(lx * 0.073 + seedOff) * step * 0.07;
    const pt = this.hellRipEdgePoint(edge, lx + jitter);
    c.save();
    c.translate(pt.x, pt.y);
    c.globalAlpha = edgeAmt * fade * flameFade;
    this.drawHellRipFlame(c, hy, flameFade, pulse, t, lx * 12.9898 + seedOff);
    c.restore();
  }
};

Game.prototype.drawHellRipRingFlames = function (c, hr, fade, pulse, t, hy, drawFrac, seedOff) {
  const r = hr.r * drawFrac;
  if (r < 8) return;
  const flameFade = clamp(drawFrac * 1.35, 0, 1);
  const circ = TAU * r;
  const step = this.hellRipFlameStep() * 0.88;
  const n = Math.max(10, Math.ceil(circ / step));
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * TAU;
    const pt = this.hellRipRingPoint(hr, ang);
    c.save();
    c.translate(pt.x, pt.y);
    c.globalAlpha = fade * flameFade;
    this.drawHellRipFlame(c, hy, flameFade, pulse, t, i * 17.31 + seedOff);
    c.restore();
  }
};

Game.prototype.hrAudioKey = function (hr) {
  return `hr:${Math.round(hr.x)}:${Math.round(hr.y)}`;
};

Game.prototype.syncHellRipLoopAudio = function (prev, next) {
  const path = spellSfx("firewall").loop;
  if (!path) return;
  const prevList = prev || [];
  const nextList = next || [];
  const prevBurning = new Set(prevList.filter(h => h.arm <= 0).map(h => this.hrAudioKey(h)));
  for (const h of nextList) {
    if (h.arm > 0) continue;
    const k = this.hrAudioKey(h);
    if (!prevBurning.has(k)) Sound.startFirewallLoop(k, path);
  }
  const nextBurning = new Set(nextList.filter(h => h.arm <= 0).map(h => this.hrAudioKey(h)));
  for (const k of prevBurning) {
    if (!nextBurning.has(k)) Sound.stopFirewallLoop(k);
  }
};

Game.prototype.drawHellRips = function () {
  const c = this.ctx;
  const t = this.nowS;
  const hy = Game.HR_THICK;
  const edgeFX = [];

  for (const hr of this.hellrips || []) {
    const drawFrac = this.hellRipDrawFrac(hr);
    const fade = this.hellRipFade(hr);
    const pulse = 0.5 + 0.5 * Math.sin(t * 8 + (hr.pulse || 0));
    const flameFade = clamp(drawFrac * 1.35, 0, 1);
    const ringR = hr.r;

    if (drawFrac > 0.08) {
      this.drawHellRipVoidCore(c, hr, fade, t);
    }

    if (drawFrac > 0.08) {
      const open = drawFrac;
      const swirlInner = ringR * 1.02;
      const swirlOuter = ringR * (1.22 + 0.22 * open);
      this.drawArenaTwistField(c, {
        cx: hr.x, cy: hr.y, coreR: swirlInner, outerR: swirlOuter,
        open: drawFrac, fade, t,
        pulse: hr.pulse || 0, armed: hr.arm <= 0,
      });
    }

    if (drawFrac > 0.1) {
      for (const edge of this.pentagramEdges(hr)) {
        edgeFX.push({ hr, edge, drawFrac, fade, pulse, flameFade });
      }
    }
  }

  for (const fx of edgeFX) {
    const { hr, edge, drawFrac, fade, pulse, flameFade } = fx;
    const visHx = edge.hx * drawFrac;
    const seedOff = hr.pulse || 0;
    this.drawHellRipEdgeFlames(c, edge, visHx, fade, pulse, t, hy, flameFade, seedOff);
  }

  for (const hr of this.hellrips || []) {
    const drawFrac = this.hellRipDrawFrac(hr);
    const fade = this.hellRipFade(hr);
    const pulse = 0.5 + 0.5 * Math.sin(t * 8 + (hr.pulse || 0));
    if (drawFrac < 0.12) continue;
    this.drawHellRipRingFlames(c, hr, fade, pulse, t, hy, drawFrac, hr.pulse || 0);
  }

  for (const hr of this.hellrips || []) {
    const fade = this.hellRipFade(hr);
    for (const p of (hr.emberParts || [])) {
      this.drawHellRipEmber(c, p, fade);
    }
  }

  c.globalCompositeOperation = "source-over";
  c.globalAlpha = 1;
  c.shadowBlur = 0;
};
